// @ts-strict-ignore
import * as React from 'react';
import { releaseStepDetailsContainerModuleStyle, antGrey, titleStyle } from '../styles/globalStyles';
import { FileDoneOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import BetaWhatToTestDialog from './BetaWhatToTestDialog';
import { IntegrationType, LocalizedBetaTestingNotes } from '../../types';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import AutomatedByRunwayIcon from '../AutomatedByRunwayIcon';
import MarkdownComponent from '../HOCs/Markdown';

interface BetaWhatToTestProps extends ReleaseComponentProps {
    externalBuildId?: string;
    betaNotes: LocalizedBetaTestingNotes[];
    betaIntegrationType?: IntegrationType;
    editTestInformationDisabled: boolean;
    shouldHideEditTestInformationButton?: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    autoApplyBetaBuildNotesAutomationIsActive: boolean;
}

const BetaWhatToTestModule: React.FC<BetaWhatToTestProps> = (props: BetaWhatToTestProps) => {
    const titleTextStyle: React.CSSProperties = {
        ...titleStyle,
        color: antGrey.grey7,
        fontSize: 12,
    };

    const [whatToTestDialogVisible, setWhatToTestDialogVisible] = React.useState(false);

    const englishBetaNoteLocale = props.betaNotes.find((betaNotes) => betaNotes.locale === 'en-US');
    const defaultBetaNoteLocale =
        englishBetaNoteLocale ? englishBetaNoteLocale
        : props.betaNotes.length > 0 ? props.betaNotes[0]
        : null;

    const titleText = () => {
        switch (props.betaIntegrationType) {
            case IntegrationType.AppCenterBeta:
                return 'Tester notes';
            case IntegrationType.TestFlight:
                return 'What to test';
            case IntegrationType.GoogleBeta:
            case IntegrationType.FirebaseBeta:
                return 'Release notes';
            default:
                return 'Testing notes';
        }
    };

    return (
        <div style={{ ...releaseStepDetailsContainerModuleStyle, backgroundColor: antGrey.grey2, overflow: 'hidden' }}>
            <Space direction="vertical" size={12}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Space>
                        {props.autoApplyBetaBuildNotesAutomationIsActive && <AutomatedByRunwayIcon />}
                        <Text style={{ ...titleTextStyle }}>{titleText()}</Text>
                    </Space>
                    {!props.shouldHideEditTestInformationButton && (
                        <Button
                            type="link"
                            icon={<FileDoneOutlined />}
                            onClick={() => setWhatToTestDialogVisible(true)}
                            disabled={props.editTestInformationDisabled || !props.betaIntegrationType}
                        >
                            Edit test information
                        </Button>
                    )}
                </div>
                {props.betaNotes.length !== 0 && defaultBetaNoteLocale?.notes && defaultBetaNoteLocale?.notes !== '' ?
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: 8,
                            gap: 12,
                            flexDirection: 'column',
                        }}
                    >
                        {defaultBetaNoteLocale.displayLocale != '' && (
                            <Text strong>{defaultBetaNoteLocale.displayLocale}</Text>
                        )}
                        <MarkdownComponent source={defaultBetaNoteLocale.notes} />
                        {props.betaNotes.length > 1 && (
                            <Button
                                type="link"
                                onClick={() => setWhatToTestDialogVisible(true)}
                                style={{ paddingLeft: 0 }}
                            >
                                ... and {props.betaNotes.length - 1} more languages
                            </Button>
                        )}
                    </div>
                :   'No testing notes set'}
            </Space>
            <BetaWhatToTestDialog
                stepRunComponentProps={props.stepRunComponentProps}
                orgId={props.orgId}
                appId={props.appId}
                releaseId={props.releaseId}
                open={whatToTestDialogVisible}
                onCancel={() => setWhatToTestDialogVisible(false)}
                onSaveSuccess={() => setWhatToTestDialogVisible(false)}
                betaNotes={defaultBetaNoteLocale}
                externalBuildId={props.externalBuildId}
                betaIntegrationType={props.betaIntegrationType}
                androidBetaNotes={props.betaNotes}
            />
        </div>
    );
};

export default BetaWhatToTestModule;
